import { round } from "lodash";
import {
    CatalogItem,
    LineItemTemplate,
    Package,
    PartnerPackage,
    ProductCatalogItem,
    ProductObject,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleActionObject,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    PurchaseRuleLinkTag,
    PurchaseRuleModifierOperation,
    PurchaseRuleShortcutType,
    Seller,
    Tag,
} from "types";
import { number, object, ref } from "yup";
import { useCollection } from "../../../hooks/useCollection";
import { isChoiceAddOn } from "../../../models/CatalogItem";

export interface BasicDiscountTerm {
    termType: TermsType;
    discount: number;
    includeAddons: BasicDiscountAddonTerm;
}

export enum TermsType {
    BASIC = "basic",
    ADVANCED = "advanced",
}

export enum BasicDiscountAddonTerm {
    INCLUDING_ADD_ONS = "including_add_ons",
    EXCLUDING_ADD_ONS = "excluding_add_ons",
}

export interface DiscountPricingValue {
    action: PurchaseRuleAction;
}

interface BasePricingFormValues {
    shortcutType: PurchaseRuleShortcutType;
    discount: number;
    finalPrice?: number;
}

export interface PricingSelectorFormValues extends BasePricingFormValues {
    catalogItem: ProductCatalogItem;
    basePrice: number;
    items?: PricingSelectorFormValues[];
}

export interface DemographicPricingFormValues extends BasePricingFormValues {
    catalogItem: ProductCatalogItem;
}

export const pricingSelectorValidationSchema = object().shape({
    basePrice: number().nullable(true),
    absolutePerUnit: number().when("shortcutType", {
        is: PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
        then: number()
            .typeError("must be a number")
            .min(0, "should be zero or a positive number")
            .max(ref("basePrice"), "must be less than Base Price value"),
    }),
    percent: number().when("shortcutType", {
        is: PurchaseRuleShortcutType.PERCENT,
        then: number()
            .typeError("must be a number")
            .min(0, "should be zero or a positive number")
            .max(100, "must be less than 100")
            .test("decimals only", "must contain decimals only", (value) => /^\d*(\.\d{0,2})?$/.test(`${value}`)),
    }),
    absolutePerPurchase: number().when("shortcutType", {
        is: PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE,
        then: number()
            .required("is required")
            .min(0, "should be zero or a positive number")
            .typeError("must be a number")
            .max(ref("basePrice"), "must be less than Base Price value"),
    }),
});

export const useLineItemTemplateByCode = (code: string) => {
    const {
        data: [lineItemTemplate],
        isLoading,
    } = useCollection<LineItemTemplate>("lineItemTemplates", { code: code, limit: 1 });
    if (isLoading) {
        return { data: null, isLoading };
    }

    return { data: lineItemTemplate, isLoading };
};

export const getDefaultAction = (): PurchaseRuleAction => {
    return {
        object: PurchaseRuleActionObject.UPSERT_LINE_ITEM_ACTION,
        shortcut: {
            type: PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
            amount: 0,
            options: {
                templateIds: [],
            },
        },
    };
};

export const getDefaultInsertAction = (discountLineTemplate: LineItemTemplate | null): PurchaseRuleAction => {
    return {
        template: { id: discountLineTemplate?.id ?? "" },
        object: PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION,
        shortcut: {
            type: PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE,
            amount: 0,
            operation: PurchaseRuleModifierOperation.SET,
        },
    };
};

export const getPurchaseAction = (purchaseRule?: PurchaseRule, templateId?: ID) => {
    if (templateId && purchaseRule) {
        const purchaseRuleAction = purchaseRule.actions.find((action) =>
            action.shortcut?.options?.templateIds?.includes(templateId),
        );

        return purchaseRuleAction;
    }
};

export const getPackageDiscountAction = (
    values: BasePricingFormValues,
    action: PurchaseRuleAction,
    catalogItem: ProductCatalogItem,
    discountTemplate: LineItemTemplate | null,
) => {
    const actionState = action.id ? { ...action } : getDefaultAction();

    const upsertLineShortcut = {
        ...action.shortcut,
        options: { templateIds: [catalogItem.template?.id ?? ""] },
        field: "amount",
    };

    const discountLineTemplate = { id: discountTemplate?.id ?? "" };

    let shortcut = { type: PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT, amount: 0 };

    switch (values.shortcutType) {
        case PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT:
            shortcut = {
                type: PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
                amount: -1 * values.discount,
            };
            break;
        case PurchaseRuleShortcutType.PERCENT:
            shortcut = { type: PurchaseRuleShortcutType.PERCENT, amount: -1 * values.discount };
            break;
        case PurchaseRuleShortcutType.ABSOLUTE_FINAL_PRICE:
            shortcut = {
                type: PurchaseRuleShortcutType.ABSOLUTE_FINAL_PRICE,
                amount: Math.abs(values.discount),
            };
            break;

        default:
            break;
    }

    actionState.template = discountLineTemplate;
    actionState.shortcut = {
        ...upsertLineShortcut,
        ...shortcut,
        operation:
            values.shortcutType === PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE
                ? PurchaseRuleModifierOperation.SET
                : PurchaseRuleModifierOperation.INC,
    };

    return actionState;
};

export const calculateDiscount = (
    price: number,
    discount: number,
    shortcutType: PurchaseRuleShortcutType,
    experiencesCount: number,
    isAddOn: boolean,
) => {
    switch (shortcutType) {
        case PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT:
            return isAddOn ? discount : discount * experiencesCount;
        case PurchaseRuleShortcutType.PERCENT:
            return round((price * discount) / 100, 2);
        case PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE:
            return isAddOn ? discount : discount * experiencesCount;
        default:
            return 0;
    }
};

export const findCatalogItem = (catalogItems: ProductCatalogItem[], templateId: ID): ProductCatalogItem | undefined => {
    return catalogItems?.find((item) => item.template?.id === templateId);
};

export const getShortcutType = (action: PurchaseRuleAction) => {
    return action?.shortcut?.type ?? PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT;
};

export const getPackagePurchaseRuleLinkPayload = (
    seller: Seller,
    pkg: Package | PartnerPackage,
    purchaseRule?: PurchaseRule,
    purchaseRuleLink?: PurchaseRuleLink,
    basicDiscount?: BasicDiscountTerm,
    existingPurchaseRuleLink?: PurchaseRuleLink | null,
): PurchaseRuleLink => {
    const sequence = purchaseRuleLink?.id ? purchaseRuleLink.purchaseRules[0].sequence : 40_100_000;
    const doesActionExist = (purchaseRule?.actions.length ?? 0) > 1;
    let allTags: Tag[] = purchaseRuleLink?.tags ?? [{ id: PurchaseRuleLinkTag.PRICING }];
    if (!existingPurchaseRuleLink && !basicDiscount && !doesActionExist) {
        const tagsToAdd: Tag[] = [{ id: TermsType.BASIC }, { id: BasicDiscountAddonTerm.INCLUDING_ADD_ONS }];
        allTags = getNewTags(allTags, tagsToAdd, []);
    } else if (existingPurchaseRuleLink?.tags && doesActionExist) {
        allTags = existingPurchaseRuleLink.tags;
    } else if (basicDiscount?.termType === TermsType.BASIC) {
        const tagsToAdd: Tag[] = [{ id: TermsType.BASIC }];
        if (basicDiscount?.includeAddons) {
            tagsToAdd.push({ id: basicDiscount.includeAddons });
        }

        allTags = getNewTags(allTags, tagsToAdd, [
            { id: TermsType.ADVANCED },
            { id: BasicDiscountAddonTerm.EXCLUDING_ADD_ONS },
            { id: BasicDiscountAddonTerm.INCLUDING_ADD_ONS },
        ]);
    } else {
        const tagsToRemove: Tag[] = [
            { id: TermsType.BASIC },
            { id: BasicDiscountAddonTerm.EXCLUDING_ADD_ONS },
            { id: BasicDiscountAddonTerm.INCLUDING_ADD_ONS },
        ];
        allTags = getNewTags(allTags, [{ id: TermsType.ADVANCED }], tagsToRemove);
    }

    const updatedPurchaseRuleLink: PurchaseRuleLink = purchaseRuleLink?.id
        ? {
              ...purchaseRuleLink,
              purchaseRules: [{ sequence: sequence, purchaseRule: purchaseRule }],
              tags: allTags,
          }
        : {
              entities: [
                  {
                      id: pkg.id,
                      object:
                          pkg.object === ProductObject.PARTNER_PACKAGE
                              ? PurchaseRuleLinkEntityObject.PARTNER_PACKAGE
                              : PurchaseRuleLinkEntityObject.PACKAGE,
                  },
              ],
              purchaseRules: purchaseRule ? [{ sequence: sequence, purchaseRule: purchaseRule }] : [],
              seller: { id: seller.id },
              tags: allTags,
          };

    return updatedPurchaseRuleLink;
};

export const getCatalogByItem = (catalogAddOns: ProductCatalogItem[], item: CatalogItem) => {
    if (item.template?.id) {
        return catalogAddOns.find((catalogAddOn) => catalogAddOn.template?.id === item.template?.id);
    }

    return undefined;
};

const getNewTags = (tags: Tag[], tagsToAdd: Tag[], tagsToRemove: Tag[]): Tag[] => {
    const updatedTags = [...tags];

    for (const tagToRemove of tagsToRemove) {
        const index = updatedTags.findIndex((tag) => tag.id === tagToRemove.id);
        if (index !== -1) {
            updatedTags.splice(index, 1);
        }
    }

    for (const tagToAdd of tagsToAdd) {
        const existingTag = updatedTags.find((tag) => tag.id === tagToAdd.id);
        if (!existingTag) {
            updatedTags.push(tagToAdd);
        }
    }

    return updatedTags;
};

export const isBasicPricing = (purchaseRuleLinks: PurchaseRuleLink[]) => {
    return purchaseRuleLinks.some((purchaseRuleLink) =>
        purchaseRuleLink.tags?.some((tag) => tag.id === TermsType.BASIC),
    );
};

export const isAdvancedPricing = (purchaseRuleLinks: PurchaseRuleLink[]) => {
    return purchaseRuleLinks.some((purchaseRuleLink) =>
        purchaseRuleLink.tags?.some((tag) => tag.id === TermsType.ADVANCED),
    );
};

export const getInitialDiscountType = (values: PricingSelectorFormValues[] | DemographicPricingFormValues[]) => {
    const discountTypes = values.flatMap((item) => {
        return "items" in item && isChoiceAddOn(item.catalogItem)
            ? item?.items?.map((item) => item.shortcutType)
            : item.shortcutType;
    });

    return discountTypes.every((value) => value === PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT)
        ? PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT
        : discountTypes.every((value) => value === PurchaseRuleShortcutType.PERCENT)
        ? PurchaseRuleShortcutType.PERCENT
        : undefined;
};

export const getTemplateId = (action: PurchaseRuleAction) => {
    return action.shortcut?.options?.templateIds?.[0];
};
